import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, SupplierCharge, SupplierContract } from "@kundeninfo/api-client";
import { useBestaetigung } from "@kundeninfo/ui";
import { Chip, Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { apiMeldung } from "../../components/vorgangsdetail.js";
import { formatDatum, formatDezimalBetrag } from "../../format.js";
import { ZahlstatusKennzeichen } from "./paymentState.js";
import { PaymentQuestion, periodText, useWithdrawPayment } from "./shared.js";
import { SUPPLIER_TEXT_WEB as T } from "./texts.js";
import "./Suppliers.css";

/**
 * DIE FÄLLIGKEITEN eines Liefervertrags (Spec „Fälligkeitenplan“, „Zahlstatus“; Plan Aufgabe 13): die Posten mit
 * Zeitraum, Fälligkeit, Brutto (rechtsbündig) und Zahlstatus.
 *
 *  - **Zahlstatus nur, wo der Posten selbst die Zahlung ist** (fester Betrag, Abschläge — Spec „Abrechnungsweise“, Z.
 *    322/465): `ZahlstatusKennzeichen` mit „angenommen“ als leisem Zusatz. Ein gebundener Posten trägt keins — seine
 *    Eingangsrechnung („E 17 · 4711“) steht in der Spalte Beleg und hat den Zahlstatus; ein ungebundener Posten bei
 *    „Rechnung je Zeitraum“ trägt das neutrale Kennzeichen „Rechnung erwartet“ (er ist eine erwartete Rechnung, keine
 *    Zahlung).
 *  - **Ordnung:** das Schlechteste oben (überfällig, dann fällig — nur Posten mit eigener Zahlung), sonst in der
 *    Ordnung des Plans. Entfallene Posten (`CANCELLED`) zählen nur als Zeile darunter.
 *  - **Zahlung bestätigen** gibt es an einem ungebundenen, offenen, teilbezahlten oder angenommen bezahlten Posten mit
 *    eigener Zahlung — die gemeinsame Zahlungsfrage (`PaymentQuestion`, shared.tsx): die EINE Zahlung, gesendet als
 *    Summe in Cent; Betrag 0 ist der Widerspruch („Lastschrift zurückgegangen“).
 *  - **Zahlung zurücknehmen** (Aufgabe 33) gibt es an jedem ungebundenen, nicht entfallenen Posten mit gespeicherter
 *    Zahlung (auch einem Widerspruch) — auch bei „Rechnung je Zeitraum“: Dort räumt sie eine vor dem Wechsel der
 *    Abrechnungsweise erfasste Zahlung weg (B-W4, der Server erlaubt nur die Rücknahme). Gestapelte Frage
 *    (`useWithdrawPayment`, shared.tsx); danach liest die Liste neu, der Posten steht wieder angenommen bezahlt oder offen.
 *  - **Außerhalb der Laufzeit** trägt ein Posten, der nach einer Kündigung gebunden stehen blieb, als Kennzeichen.
 *  - **Neu berechnen** baut den Plan neu (feste Posten bleiben) — eine direkte Aktion, also gefragt.
 *  - Nach einer Zahlung liegt der Fokus auf der Zeile des Postens (der Knopf ist dann womöglich fort; Final-Fixrunde
 *    A-K8) — nicht auf dem Seitenrumpf.
 *
 * Der Plan wird bei jedem `refresh` neu gelesen: Nach jedem Schreibweg — auch einer eigenen Aktion, auch einer
 * gescheiterten — lädt die Liste neu (`onChanged`), und ihr neuer Stand liest den Plan; nie steht ein veralteter Posten
 * da (Lehre aus Review 17, W1). Ein Ladefehler ist kein leerer Plan.
 */
export type ChargeListClient = Pick<ApiClient, "getSupplierCharges" | "confirmChargePayment" | "recalculateSupplierCharges">;

/** Wo der Posten selbst die Zahlung ist (Spec „Abrechnungsweise“): fester Betrag und Abschläge, ungebunden. */
const PAYABLE_MODES: readonly SupplierContract["billingMode"][] = ["FIXED_PAYMENT", "INSTALMENT_SETTLEMENT"];
const ownPayment = (contract: SupplierContract, c: SupplierCharge): boolean =>
  PAYABLE_MODES.includes(contract.billingMode) && c.incomingInvoiceId === null;
const payable = (contract: SupplierContract, c: SupplierCharge): boolean =>
  ownPayment(contract, c) && c.status === "PLANNED";
/** Eine gespeicherte Zahlung an einem ungebundenen, nicht entfallenen Posten — gleich welcher Abrechnungsweise (B-W4). */
const withdrawable = (c: SupplierCharge): boolean =>
  c.paidAmount !== null && c.incomingInvoiceId === null && (c.status === "PLANNED" || c.status === "PAID");

const RANK = { ERROR: 0, WARN: 1, OK: 2 } as const;
/** Die Stufe zählt nur bei eigener Zahlung — ein „überfälliger“ Posten, der eine Rechnung erwartet, steht nicht oben. */
const rank = (contract: SupplierContract, c: SupplierCharge): number =>
  (!ownPayment(contract, c) || c.payment.level === null ? 2 : RANK[c.payment.level]);

export function ChargeList(p: {
  customerId: string; contract: SupplierContract; client: ChargeListClient; refresh: number; locked: boolean;
  onChanged: () => void;
}): ReactElement {
  const { client, customerId, contract } = p;
  const [items, setItems] = useState<SupplierCharge[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [paying, setPaying] = useState<SupplierCharge | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [focusAfter, setFocusAfter] = useState<string | null>(null);
  const root = useRef<HTMLDivElement | null>(null);
  const { bestaetigen } = useBestaetigung();
  const withdraw = useWithdrawPayment();
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    if (focusAfter === null) return;
    const row = Array.from(root.current?.querySelectorAll<HTMLElement>("tr[data-charge-id]") ?? [])
      .find((r) => r.dataset.chargeId === focusAfter);
    if (row === undefined) return;
    setFocusAfter(null);
    row.focus({ preventScroll: true });
  }, [items, focusAfter]);

  useEffect(() => {
    let alive = true;
    client.getSupplierCharges(customerId, contract.id).then((r) => {
      if (!alive) return;
      setItems(r.items);
      setFailed(false);
    }, () => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [client, customerId, contract.id, p.refresh]);

  async function recalculate(): Promise<void> {
    if (!(await bestaetigen({ titel: T.confirmRecalculate, text: T.recalculateText, bestaetigenText: T.recalculate }))) return;
    setNotice(null);
    try {
      await client.recalculateSupplierCharges(customerId, contract.id);
    } catch (e) {
      setNotice(apiMeldung(e, T.actionFailed));
    }
    p.onChanged();
  }

  async function withdrawPayment(c: SupplierCharge): Promise<void> {
    setWithdrawing(true);
    const r = await withdraw({ paidAmount: c.paidAmount!, paidOn: c.paidOn, currency: contract.currency,
      run: () => client.confirmChargePayment(customerId, contract.id, c.id, { version: c.version, paidOn: null, paidAmount: null }) });
    setWithdrawing(false);
    if (r === null) return;
    setNotice(r.failure);
    if (r.failure === null) setFocusAfter(c.id);
    p.onChanged();
  }

  const shown = (items ?? []).filter((c) => c.status !== "CANCELLED");
  const ordered = shown.map((c, i) => ({ c, i }))
    .sort((a, b) => rank(contract, a.c) - rank(contract, b.c) || a.i - b.i).map((x) => x.c);
  const cancelled = (items ?? []).length - shown.length;
  const money = (amount: string): string => formatDezimalBetrag(amount, contract.currency);

  return (
    <div ref={root} className="sup__charges">
      {notice !== null && <Hinweis art="fehler">{notice}</Hinweis>}
      {failed && <Hinweis art="fehler">{T.chargesLoadFailed}</Hinweis>}
      {items === null && !failed && <p className="sup__muted" role="status">{T.loading}</p>}
      {items !== null && !failed && shown.length === 0 && <p className="sup__muted">{T.noCharges}</p>}
      {items !== null && !failed && ordered.length > 0 && (
        <Tabelle dicht stapeln>
          <table>
            <thead>
              <tr>
                <th scope="col">{T.columnPeriod}</th>
                <th scope="col">{T.columnDue}</th>
                <th scope="col" data-ausrichtung="rechts">{T.columnGross}</th>
                <th scope="col">{T.columnPayment}</th>
                <th scope="col">{T.columnDocument}</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((c) => {
                const period = periodText(c.subperiodStart, c.subperiodEnd);
                return (
                  <tr key={c.id} data-charge-id={c.id} tabIndex={-1}>
                    <td>{period}</td>
                    <td>{formatDatum(c.dueDate)}</td>
                    <td data-ausrichtung="rechts">{money(c.grossAmount)}</td>
                    <td>
                      <span className="sup__marks">
                        {ownPayment(contract, c) && <ZahlstatusKennzeichen payment={c.payment} />}
                        {c.incomingInvoiceId === null && !ownPayment(contract, c) && (
                          <Chip statusArt="aus">{T.invoiceExpected}</Chip>
                        )}
                        {c.outsideTerm && <Chip statusArt="warnung">{T.outsideTerm}</Chip>}
                      </span>
                    </td>
                    <td>
                      {c.incomingInvoiceLabel !== null && <span>{c.incomingInvoiceLabel}</span>}
                      {c.incomingInvoiceLabel === null && c.paidOn !== null && c.paidAmount !== null && (
                        <span className="sup__muted">{T.paidLine(money(c.paidAmount), formatDatum(c.paidOn))}</span>
                      )}
                      {payable(contract, c) && (
                        <KiKnopf kompakt deaktiviert={p.locked || paying !== null || withdrawing} name={T.payFor(period)}
                          onClick={() => setPaying(c)}>{T.payAction}</KiKnopf>
                      )}
                      {withdrawable(c) && (
                        <KiKnopf kompakt deaktiviert={p.locked || paying !== null || withdrawing} name={T.withdrawFor(period)}
                          onClick={() => { void withdrawPayment(c); }}>{T.withdrawAction}</KiKnopf>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Tabelle>
      )}
      <div className="sup__foot">
        {cancelled > 0 && <span className="sup__muted">{T.cancelledCount(cancelled)}</span>}
        <KiKnopf kompakt deaktiviert={p.locked} onClick={() => { void recalculate(); }}>{T.recalculate}</KiKnopf>
      </div>
      {paying !== null && (
        <PaymentQuestion currency={contract.currency} paidAmount={paying.paidAmount} payment={paying.payment}
          gross={paying.grossAmount} onDone={() => { setPaying(null); setFocusAfter(paying.id); p.onChanged(); }}
          onClose={() => setPaying(null)}
          onStale={() => { setPaying(null); p.onChanged(); }}
          run={(paidOn, paidAmount) => client.confirmChargePayment(customerId, contract.id, paying.id,
            { version: paying.version, paidOn, paidAmount })} />
      )}
    </div>
  );
}
